import { getFileContent } from "../../features/http";
import { saveProjectInLocalProjectList } from "../../features/localstorage/saver";
import { getUserCodeFromLSAsync } from "../../features/localstorage/loader";
import { getLinesOfCode } from "../../features/loccount";
import { getAppCfg } from "../app-cfg";
import { loadUserData } from "../code/loader";
import { saveUserData } from "../code/saver";
import { CONTENT_VIEW_NAME, EDITOR_VIEW_NAME, LECTURE_VIEW_NAME, LOADING_VIEW_NAME } from "../constants";
import { getDomainCfg } from "../state";
import { EditorData, ProjectData, ProjectDataTab } from "../types";
import { getViewData, setViewData, showView } from "../views";
import { shouldUseVFS, getFileDefinitions, vfs, ProjectJsonV2, FileDefinition } from "../../features/filesystem";

import he from 'he';

async function loadContentData(projectData: ProjectData): Promise<void> {
  const projectUrl = getDomainCfg().projectSettings.projectUrl;
  const contentUrl = projectUrl + projectData.content;
  const content = await getFileContent(contentUrl);
  const viewData = getViewData(CONTENT_VIEW_NAME);
  viewData.data = content;
  viewData.project = projectData;
  setViewData(CONTENT_VIEW_NAME, viewData);
}

async function loadInteractiveLectureData(projectData: ProjectData): Promise<void> {
  console.log("load interactive lecture data")
  const viewData = getViewData(LECTURE_VIEW_NAME);
  viewData.data = projectData.content;
  viewData.project = projectData;
  setViewData(LECTURE_VIEW_NAME, viewData);
}

async function loadEditorData(projectData: ProjectData, publicProject: boolean): Promise<void> {
  const projectUrl = getDomainCfg().projectSettings.projectUrl;
  const projectContent = await loadProjectFileContents(projectUrl, projectData);
  const errorUrl = getDomainCfg().projectSettings.errorFile;
  const errorContent = await loadErrorFileContent(errorUrl);

  const viewData = getViewData(EDITOR_VIEW_NAME);
  const newData = { ...viewData };

  newData.data = {
    projectContent,
    errorContent
  }

  if (getDomainCfg().projectSettings.codeFromCfgEnabled && getAppCfg().code) {
    let code = getAppCfg().code;

    // Überprüfen, ob der String HTML-Entities enthält
    const shouldDecode = code !== he.escape(code);

    // Nur dekodieren, wenn der String HTML-Entities enthält
    const decodedCode = shouldDecode ? he.decode(code) : code;

    newData.data.projectContent.projectSource = decodedCode;
  }

  newData.project = projectData;
  setViewData(EDITOR_VIEW_NAME, newData);
  console.log("Project: Editor data loaded", newData)
  await loadUserData(publicProject);
  saveProjectInLocalProjectList(newData);
  //saveUserData(); //do we really want to save just after loading an not changing anything?
}

export async function loadProjectFileContents(projectUrl: string, projectData: ProjectData): Promise<EditorData> {
  let projectSourceCode = "";
  if (projectData.code) {
    const codeUrl = `${projectUrl}/${projectData.code}`;
    projectSourceCode = await getFileContent(codeUrl);
  }

  let files: ProjectDataTab[] = [];

  // Version 2.0: Lade Dateien aus filesystem.files + LocalStorage (für hochgeladene Dateien)
  const isV2 = shouldUseVFS(projectData as any);
  if (isV2 && (projectData as any).filesystem?.files) {
    const v2Project = projectData as unknown as ProjectJsonV2;
    const projectId = getAppCfg().projectId || projectData.project?.replace(/\s+/g, '_');

    // Basis: Dateien aus project.json
    let fileDefinitions: FileDefinition[] = [...v2Project.filesystem.files];

    // Merge: Hochgeladene Dateien aus LocalStorage
    try {
      const lsData = await getUserCodeFromLSAsync(projectId);
      console.log(`[V2] Checking LS for fileDefinitions (projectId: ${projectId})`, lsData);
      if (lsData?.isV2 && lsData?.fileDefinitions) {
        // Füge Dateien hinzu, die nicht in project.json sind
        for (const lsDef of lsData.fileDefinitions) {
          const existsInProject = fileDefinitions.some(f => f.path === lsDef.path);
          if (!existsInProject) {
            fileDefinitions.push(lsDef);
            console.log(`[V2] Merged uploaded file from LS: ${lsDef.path}`);
          }
        }
      }
    } catch (e) {
      console.warn('[V2] Could not load fileDefinitions from LocalStorage:', e);
    }

    // VFS zuerst initialisieren
    let vfsAvailable = false;
    try {
      if (typeof navigator !== 'undefined' && 'storage' in navigator) {
        await vfs.init();
        vfsAvailable = true;
        console.log(`[V2] VFS initialized for project ${projectId}`);
      }
    } catch (vfsError) {
      console.warn('[V2] VFS initialization failed (OPFS may not be available):', vfsError);
    }

    // Lade alle Dateien - VFS hat Priorität, dann Server als Fallback
    const loadPromises = fileDefinitions.map(async (fileDef, index) => {
      let fileContent = '';
      const vfsPath = `${vfs.getProjectRoot(projectId)}/${fileDef.path}`;

      // 1. Versuche VFS zu lesen (persistierte Änderungen)
      if (vfsAvailable) {
        try {
          if (await vfs.exists(vfsPath)) {
            fileContent = await vfs.readFile(vfsPath);
            console.log(`[V2] Loaded from VFS: ${fileDef.path}`);
          }
        } catch (e) {
          console.warn(`[V2] VFS read failed for ${fileDef.path}:`, e);
        }
      }

      // 2. Fallback: Vom Server laden (initialer Inhalt)
      if (!fileContent) {
        const fileUrl = `${projectUrl}/${fileDef.path}`;
        try {
          fileContent = await getFileContent(fileUrl);
          console.log(`[V2] Loaded from server: ${fileDef.path}`);

          // In VFS schreiben für nächstes Mal
          if (vfsAvailable) {
            try {
              await vfs.writeFile(vfsPath, fileContent);
            } catch (e) {
              console.warn(`[V2] Could not write to VFS: ${fileDef.path}:`, e);
            }
          }
        } catch (e) {
          console.warn(`[V2] Could not load file ${fileDef.path}:`, e);
        }
      }

      return {
        index,
        code: fileContent,
        name: fileDef.name,
        language: fileDef.language,
        active: fileDef.active === true,
        replaceKey: fileDef.replaceKey,
        path: fileDef.path // Extra Property für VFS
      } as ProjectDataTab;
    });

    files = await Promise.all(loadPromises);
  }
  // Legacy: Lade Dateien aus tabs[]
  else if (projectData.tabs !== undefined && projectData.tabs.length > 0) {
    const loadPromises = projectData.tabs.map(async (tab, index) => {
      const tabContentUrl = `${projectUrl}/${tab.code}`;
      const fileSourceCode = await getFileContent(tabContentUrl);
      const tabIndex = tab.index || index;
      return {
        index: tabIndex,
        code: fileSourceCode,
        name: tab.name,
        language: tab.language,
        active: (tab.active === true),
        replaceKey: tab.replaceKey
      } as ProjectDataTab;
    });
    files = await Promise.all(loadPromises);
  }

  let templateSourceCode;
  if (projectData.template) {
    const templateUrl = `${projectUrl}/${projectData.template}`;
    templateSourceCode = await getFileContent(templateUrl);
  }

  let injectJsCode = "";
  if (projectData.injectJs && String(projectData.injectJs).trim()) {
    const injectUrl = `${projectUrl}/${projectData.injectJs}`;
    injectJsCode = await getFileContent(injectUrl);
  }

  let initSourceCode = "";
  if (projectData.initKey !== undefined) {
    const initCodeUrl = `${projectUrl}/${projectData.init}`;
    initSourceCode = await getFileContent(initCodeUrl);
  }
  let assetsList = "";
  if (projectData.assetsKey !== undefined) {
    const assetsUrl = `${projectUrl}/${projectData.assets}`;
    assetsList = await getFileContent(assetsUrl);
  }

  let solution = "";
  if (projectData.solution !== undefined) {
    const solutionUrl = `${projectUrl}/${projectData.solution}`;
    solution = await getFileContent(solutionUrl);
  }

  let context;
  if (getDomainCfg().projectSettings.chatContextEnabled) {
    context = await loadProjectContext(projectUrl, projectData, projectSourceCode, solution);
    //console.log("context", context)
  }

  return {
    projectSource: projectSourceCode,
    tabs: files,
    templateCode: templateSourceCode,
    initCode: initSourceCode,
    initLOC: getLinesOfCode(initSourceCode),
    projectAssets: assetsList,
    solutionCode: solution,
    contextContent: context,
    injectJsCode: injectJsCode || undefined
  }
}

export const loadProjectContext = async (projectUrl, project, sourceCode, solutionCode) => {
  //check if project has fields: context, contextSelector, contextUrl or contextFile
  //load context from contextFile
  let userContext = "";
  if (project.contextFile) {
    const contextUrl = `${projectUrl}/${project.contextFile}`;
    const context = await getFileContent(contextUrl);
    const contextFromFile = context;
    if (contextFromFile) {
      userContext = contextFromFile;
    }
  }

  if (project.contextUrl) {
    const contextUrl = project.contextUrl;
    const context = await getFileContent(contextUrl);
    const contextFromUrl = context;
    if (contextFromUrl) {
      userContext += contextFromUrl;
    }
  }

  if (project.contextSelector) {
    //get element by selector on the current page
    const contextSelector = project.contextSelector || getDomainCfg().projectSettings.contextSelector;
    const contextElement = document.querySelector(contextSelector);
    let contextFromElement = null;
    //format as text from html
    if (contextElement) {
      contextFromElement = contextElement.innerText;
      if (contextFromElement) {
        userContext += contextFromElement;
      }
    }
  }

  if (project.context) {
    userContext += project.context;
  }

  if (project.useProjectContext || getDomainCfg().projectSettings.chatContextEnabled) {
    let projectContext = ''
    projectContext += 'Programmiersprache des Projekts: ' + project.language + '\n ----- \n';
    if (sourceCode) {
      projectContext += 'Quellcode (Das ist der Startcode, den der Benutzer bearbeiten soll): ' + sourceCode + '\n ----- \n';
    }
    if (project.task) {
      projectContext += 'Aufgabe (Hauptaufgabe des Projekts für den Benutzer): ' + project.task + '\n ----- \n';
    }
    if (project.subTasks) {
      projectContext += 'Unteraufgaben (diese Aufgaben muss der Benutzer lösen, also nur zur Prüfung verwenden!): ' + JSON.stringify(project.subTasks) + '\n ----- \n';
    }
    if (solutionCode) {
      projectContext += 'Lösung (nie einfach herausgeben, sondern nur zur Prüfung verwenden!):  ' + solutionCode + '\n ----- \n';
    }
    userContext += projectContext;
  }

  return userContext;
}

export const loadErrorFileContent = async (errorUrl: string): Promise<string> => {
  return await getFileContent(errorUrl);
}

export async function loadContentProject(projectData: ProjectData): Promise<void> {
  console.log("load content project")
  loadContentData(projectData);
}

export async function loadInteractiveLecture(projectData: ProjectData): Promise<void> {
  console.log("load lecture project")
  loadInteractiveLectureData(projectData);
}


export async function loadEditorProject(projectData: ProjectData, publicProject: boolean): Promise<void> {
  console.log("load editor project")
  await loadEditorData(projectData, publicProject);
}

export function showLoadingView(): void {
  showView(LOADING_VIEW_NAME);
}
